import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../utils/cn";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
  iconOnly?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-primary-500 text-white hover:bg-primary-600 focus-visible:ring-primary-400/60 disabled:bg-primary-300",
  secondary:
    "bg-secondary-500 text-white hover:bg-secondary-600 focus-visible:ring-secondary-400/60 disabled:bg-secondary-300",
  outline:
    "border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100 focus-visible:ring-primary-400/40 disabled:text-foreground-400",
  ghost:
    "text-foreground-700 hover:bg-background-100 focus-visible:ring-primary-400/40 disabled:text-foreground-400",
  danger: "bg-danger-500 text-white hover:bg-danger-600 focus-visible:ring-danger-400/60 disabled:bg-danger-300",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-xs gap-1.5 rounded-md",
  md: "h-9 px-4 text-sm gap-2 rounded-md",
  lg: "h-11 px-5 text-sm gap-2 rounded-lg",
};

const ICON_SIZES: Record<Size, string> = {
  sm: "w-8",
  md: "w-9",
  lg: "w-11",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    icon,
    fullWidth = false,
    iconOnly = false,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex cursor-pointer items-center justify-center whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        iconOnly && ICON_SIZES[size],
        fullWidth && "w-full",
        className
      )}
      {...rest}
    >
      {loading ? (
        <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
      ) : (
        icon
      )}
      {!iconOnly ? <span>{children}</span> : null}
    </button>
  );
});